import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useMotionValue, animate } from 'framer-motion';
import { useConversation } from '@/app/providers/ConversationContext';
import { readSessionPersisted, writeSessionPersisted } from '@/common/hooks/useSessionPersistedState';
import { computeHourHeight } from '../hourHeightComputation';
import { DAY_GRID_VISIBLE_HOURS } from '../calendarConstants';
import { calendarFlash } from '@/lib/calendarFlash';

/**
 * useCalendarNavigation — sizing + swipe + expandedView + dateTime clusters for
 * EventListModule (#3279). Owns: Day/Week/Month sizing measurements + the three
 * layout refs; mobile swipe-gesture navigation (swipeRef + touchstart/touchend →
 * setCalendarDate); the expandedView cluster (`isExpandedView`, true inside a
 * `.chat-box-module-fullscreen` ancestor, plus the ancestor height-override
 * effects); and the navigation date/view state (`calendarDate`, `timeFilter`,
 * `expandedDate`). Called EXACTLY ONCE near the top of EventListModule.
 *
 * INVARIANTS:
 *   - The Day/Week body-height measurement MUST stay a `useLayoutEffect` so it
 *     re-measures synchronously after DOM mutation, before paint (#1541).
 *   - localStorage (`xavior:calendar_time_filter_<moduleId>`) is the SOURCE OF
 *     TRUTH for the view preference across the module's keyed remount; the
 *     conversation-config mirror is best-effort only. localStorage being
 *     authoritative replaced the old config-only sync that lost a remount race
 *     (#3300/#3301).
 *   - `calendarDate` (the viewed anchor date) is likewise restored SYNCHRONOUSLY
 *     on remount via `useSessionPersistedState` (`xavior:calendar_date_<moduleId>`,
 *     sessionStorage — tab-scoped, unlike the cross-session timeFilter pref), so
 *     returning to Calendar lands back where the user was instead of snapping to
 *     today. Stored as an epoch-ms number (a `Date` doesn't round-trip through
 *     `JSON.stringify`/`parse`); `calendarDate` itself stays a `Date` for every
 *     consumer via a thin derive/wrap around the persisted ms value.
 *   - The swipe effect uses the functional `setCalendarDate(prev => …)` form so
 *     its dep array stays `[isMobile, timeFilter]`.
 *
 * `calendarNumWeeks` is computed only to feed the ancestor style-apply effect's
 * dep array; `monthCellWidth` is returned but currently unread by the caller.
 */
export interface UseCalendarNavigationParams {
  isMobile: boolean;
  // `module.config` — seeds the initial timeFilter (legacy fallback only; the
  // localStorage key is now the source of truth). Typed loosely (the orchestrator
  // reads `config?.timeFilter`).
  moduleConfig: unknown;
  moduleId: string;
  conversationId: string;
}

export interface UseCalendarNavigation {
  dayWeekBodyHeight: number;
  monthCellWidth: number;
  isExpandedView: boolean;
  calendarDate: Date;
  setCalendarDate: Dispatch<SetStateAction<Date>>;
  timeFilter: 'today' | 'week' | 'month';
  setTimeFilter: Dispatch<SetStateAction<'today' | 'week' | 'month'>>;
  expandedDate: Date | null;
  setExpandedDate: Dispatch<SetStateAction<Date | null>>;
  // `useRef<HTMLDivElement>(null)` infers `RefObject<HTMLDivElement | null>` in
  // React 19; CalendarViewSelector's ref props use the same nullable shape.
  timeGridRef: RefObject<HTMLDivElement | null>;
  dayWeekContainerRef: RefObject<HTMLDivElement | null>;
  monthGridRef: RefObject<HTMLDivElement | null>;
  calendarContainerRef: RefObject<HTMLDivElement | null>;
  swipeRef: RefObject<{ startX: number; startY: number; startTime: number } | null>;
  moduleRootRef: RefObject<HTMLDivElement | null>;
}

// Ancestor DOM snapshot: the module-root element + the ancestor chain whose
// inline sizing styles we override (and the saved originals to restore on
// unmount). Kept identical to the shape previously declared inline in
// EventListModule.
interface AncestorState {
  el: HTMLElement;
  ancestors: HTMLElement[];
  saved: { height: string; maxHeight: string; minHeight: string; flex: string }[];
  savedRoot: { height: string; maxHeight: string; minHeight: string; flex: string };
}

export function useCalendarNavigation(
  params: UseCalendarNavigationParams,
): UseCalendarNavigation {
  const {
    isMobile,
    moduleConfig,
    moduleId,
    conversationId,
  } = params;

  const { updateConversationModules } = useConversation();

  // Per-module view preference. Persisted in localStorage (synchronous) rather
  // than the conversation config, because EventListModule is keyed by module.id
  // and so fully remounts when you navigate away and back — a localStorage read
  // in the initializer survives that instantly, with no async round-trip to race
  // against. Mirrors the contacts module's `contacts_grid_sort_<id>` pattern.
  const timeFilterStorageKey = `xavior:calendar_time_filter_${moduleId}`;
  // Viewed anchor date. sessionStorage (not localStorage) — tab-scoped, since
  // "where you were looking" shouldn't leak across separate browser sessions
  // the way the Day/Week/Month view preference does.
  const calendarDateStorageKey = `xavior:calendar_date_${moduleId}`;

  // dateTime cluster: the navigation date/view state the rest of the module reads
  // back. Declared early so they drive the fetch window in EventListModule and feed
  // the sizing/swipe effects below. `timeFilter` seeds from localStorage first,
  // falling back to any legacy value persisted on `moduleConfig.timeFilter`.
  //
  // `calendarDate` is persisted as epoch ms (a `Date` isn't directly JSON-safe —
  // `JSON.stringify`/`parse` round-trips it to a string, not a `Date`) via
  // `useSessionPersistedState`, then derived back into a `Date` so every existing
  // consumer (prev/next/today/swipe handlers, effects) keeps working unchanged.
  // Plain useState so setCalendarDateMs stays a direct (stable) useState setter —
  // setCalendarDate wraps it in a useCallback that is listed in downstream effect
  // deps, and a custom-hook setter would make that useCallback (and its consumers)
  // trip react-hooks/exhaustive-deps. Restore is a lazy initializer; the effect
  // below persists changes.
  const [calendarDateMs, setCalendarDateMs] = useState<number>(
    () => readSessionPersisted<number>(
      calendarDateStorageKey,
      () => Date.now(),
      (v): v is number => typeof v === 'number' && Number.isFinite(v),
    ),
  );
  useEffect(() => {
    writeSessionPersisted(calendarDateStorageKey, calendarDateMs);
  }, [calendarDateStorageKey, calendarDateMs]);
  const calendarDate = useMemo(() => new Date(calendarDateMs), [calendarDateMs]);
  const setCalendarDate = useCallback<Dispatch<SetStateAction<Date>>>((action) => {
    setCalendarDateMs((prevMs) => {
      const nextDate = typeof action === 'function'
        ? (action as (prev: Date) => Date)(new Date(prevMs))
        : action;
      return nextDate.getTime();
    });
  }, [setCalendarDateMs]);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month'>(() => {
    try {
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(timeFilterStorageKey) : null;
      if (stored === 'today' || stored === 'week' || stored === 'month') return stored;
    } catch { /* ignore storage access errors */ }
    // Fall back to any legacy value persisted on the module config.
    const config = moduleConfig as any;
    return config?.timeFilter || 'month';
  });
  const [expandedDate, setExpandedDate] = useState<Date | null>(null);

  // One-shot "flash a day" intent from a chat result card (e.g. the therapy
  // answer): jump to that month + month view so MonthView can paint the day.
  // In-memory, so a refresh clears it; MonthView clears it on the first mouse move.
  useEffect(() => {
    const goTo = (iso: string) => {
      const [y, m] = iso.split('-').map(Number);
      setCalendarDate(new Date(y, m - 1, 1));
      setTimeFilter('month');
    };
    const pending = calendarFlash.get();
    if (pending) goTo(pending);
    return calendarFlash.subscribe(goTo);
    // setCalendarDate is a referentially-stable useCallback (its only dep is the
    // stable useState setter), so listing it never re-runs this effect.
  }, [setCalendarDate]);

  // Calendar view: module root + ancestor sizing override, plus the
  // expanded/split-view detection. `moduleRootRef` is attached to the module
  // root <div>; `isExpandedView` is true when an ancestor carries the
  // `.chat-box-module-fullscreen` class.
  const moduleRootRef = useRef<HTMLDivElement>(null);
  const [isExpandedView, setIsExpandedView] = useState(false);

  useEffect(() => {
    const check = () => {
      const el = moduleRootRef.current;
      setIsExpandedView(!!el?.closest('.chat-box-module-fullscreen'));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const timeGridRef = useRef<HTMLDivElement>(null);
  // #1541: container ref + measured height for Day/Week views so the time grid
  // body fills the available vertical space on cold mount (no hard refresh required).
  const dayWeekContainerRef = useRef<HTMLDivElement>(null);
  const [dayWeekBodyHeight, setDayWeekBodyHeight] = useState(0);

  // Month view: measure grid width to compute square cell row heights
  const monthGridRef = useRef<HTMLDivElement>(null);
  const [monthCellWidth, setMonthCellWidth] = useState(0);
  useEffect(() => {
    if (timeFilter !== 'month') {
      setMonthCellWidth(0);
      return;
    }
    const el = monthGridRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setMonthCellWidth(Math.floor(w / 7));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [timeFilter, calendarDate]);
  useEffect(() => {
    if (!isMobile || timeFilter !== 'month') return;
    const measure = () => {
      const el = monthGridRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const bottomBar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0') || 0;
      const bottomOffset = 50 + Math.max(6, bottomBar);
      const available = window.innerHeight - rect.top - bottomOffset;
      if (available > 0) {
        el.style.height = `${available}px`;
      }
    };
    requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (monthGridRef.current) {
        monthGridRef.current.style.height = '';
      }
    };
  }, [isMobile, timeFilter, calendarDate]);

  // #1541: Measure Day/Week container so the scrollable time grid body fills the
  // available vertical space on cold mount. Previously the body used a hardcoded
  // `12 * HOUR_HEIGHT` which rendered at ~half height on first paint until a
  // window resize forced a relayout (hard refresh as workaround).
  //
  // Strategy: observe the outer .xv-week container; subtract the heights of the
  // header + all-day rows (the siblings of the time grid body) to derive the
  // body's target height. useLayoutEffect runs synchronously after DOM mutation
  // so we re-measure before paint. ResizeObserver picks up subsequent layout
  // shifts (viewport changes, ancestor flex settling, expand/split toggles).
  useLayoutEffect(() => {
    if (!(timeFilter === 'today' || timeFilter === 'week')) {
      setDayWeekBodyHeight(0);
      return;
    }
    const el = dayWeekContainerRef.current;
    if (!el) return;
    const measure = () => {
      const totalHeight = el.getBoundingClientRect().height;
      if (totalHeight <= 0) return;
      // Sum heights of any non-body siblings (header row, all-day row).
      let nonBody = 0;
      for (const child of Array.from(el.children) as HTMLElement[]) {
        if (!child.classList.contains('xv-week-body')) {
          nonBody += child.getBoundingClientRect().height;
        }
      }
      const available = Math.max(0, Math.floor(totalHeight - nonBody));
      if (available > 0) setDayWeekBodyHeight(available);
    };
    // Measure immediately and again on next frame (covers cases where flex
    // ancestors haven't settled yet on first paint).
    measure();
    const raf = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Also watch for viewport resize as a belt-and-braces fallback.
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [timeFilter, isMobile, isExpandedView, calendarDate]);

  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef<{ startX: number; startY: number; startTime: number } | null>(null);
  useEffect(() => {
    if (!isMobile) return;
    const el = calendarContainerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      swipeRef.current = { startX: touch.clientX, startY: touch.clientY, startTime: Date.now() };
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!swipeRef.current) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - swipeRef.current.startX;
      const dy = touch.clientY - swipeRef.current.startY;
      const dt = Date.now() - swipeRef.current.startTime;
      swipeRef.current = null;

      const isHorizontal = Math.abs(dx) > Math.abs(dy) * 1.5;
      const isVertical = Math.abs(dy) > Math.abs(dx) * 1.5;

      if (timeFilter === 'month' && isVertical && Math.abs(dy) > 60 && dt < 400) {
        // Month view: swipe up → next month, swipe down → prev month (vertical stacking)
        if (dy < 0) {
          setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
        } else {
          setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
        }
      } else if (isHorizontal && Math.abs(dx) > 60 && dt < 400) {
        // Day/week views: swipe left → next, swipe right → prev (horizontal)
        if (dx < 0) {
          setCalendarDate(prev => {
            if (timeFilter === 'today') return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1);
            if (timeFilter === 'week') return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 7);
            return new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
          });
        } else {
          setCalendarDate(prev => {
            if (timeFilter === 'today') return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1);
            if (timeFilter === 'week') return new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 7);
            return new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
          });
        }
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, timeFilter, setCalendarDate]);

  const calendarNumWeeks = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const startPadding = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = startPadding + daysInMonth;
    const padded = totalCells + ((7 - (totalCells % 7)) % 7);
    return padded / 7;
  }, [calendarDate]);

  // Force ancestors to yield height so calendar content sizes correctly.
  // Separated into two effects: one to capture/release ancestors (stable across
  // month navigations), and one to apply styles (runs on every change without cleanup flash).
  // Stored ref for ancestor DOM elements and their original inline styles.
  const ancestorsRef = useRef<AncestorState | null>(null);

  // Effect 1: capture ancestors on mount, release on unmount
  useEffect(() => {
    if (!moduleRootRef.current) return;

    const el = moduleRootRef.current;
    // .menu-layout is never rendered (dead front, swept) — only the module root is sized; ancestors are never overridden.
    const targetAncestors: HTMLElement[] = [];

    const saved = targetAncestors.map((a) => ({
      height: a.style.height,
      maxHeight: a.style.maxHeight,
      minHeight: a.style.minHeight,
      flex: a.style.flex,
    }));
    const savedRoot = { height: el.style.height, maxHeight: el.style.maxHeight, minHeight: el.style.minHeight, flex: el.style.flex };
    ancestorsRef.current = { el, ancestors: targetAncestors, saved, savedRoot };

    return () => {
      if (ancestorsRef.current) {
        const { el: e, ancestors: aa, saved: ss, savedRoot: sr } = ancestorsRef.current;
        e.style.height = sr.height;
        e.style.maxHeight = sr.maxHeight;
        e.style.minHeight = sr.minHeight;
        e.style.flex = sr.flex;
        aa.forEach((a, i) => {
          a.style.height = ss[i].height;
          a.style.maxHeight = ss[i].maxHeight;
          a.style.minHeight = ss[i].minHeight;
          a.style.flex = ss[i].flex;
        });
        ancestorsRef.current = null;
      }
    };
  }, []);

  // Effect 2: apply/update styles — runs on month nav, view toggle, etc.
  // No cleanup (avoids flash between months). Styles are released by Effect 1.
  useEffect(() => {
    if (!ancestorsRef.current) return;
    const { el, ancestors } = ancestorsRef.current;

    if (timeFilter === 'month') {
      // Module root: fill available space so month grid expands
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.setProperty('flex', '1 1 auto', 'important');
      el.style.removeProperty('height');
      ancestors.forEach((a) => {
        a.style.setProperty('max-height', 'none', 'important');
        a.style.setProperty('min-height', '0', 'important');
        a.style.setProperty('flex', '1 1 auto', 'important');
        a.style.removeProperty('height');
      });
    } else {
      // Week/Day: fill viewport — just remove max-height caps, break min-height
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.removeProperty('height');
      el.style.removeProperty('flex');
      ancestors.forEach((a) => {
        a.style.setProperty('max-height', 'none', 'important');
        a.style.removeProperty('height');
        a.style.removeProperty('min-height');
        a.style.removeProperty('flex');
      });
    }
  }, [calendarNumWeeks, timeFilter]);

  // Day/week: scroll to 8 AM
  useEffect(() => {
    if (!(timeFilter === 'today' || timeFilter === 'week')) return;
    const el = timeGridRef.current;
    if (!el) return;

    // #3619: the grid scales per-hour height to the measured body height, so the
    // scroll target must use the SAME HOUR_HEIGHT the Day/Week views render with
    // (DayView/WeekView call computeHourHeight with the same visibleHours count).
    // Using the fixed base here would land the initial scroll well above 8 AM on
    // a tall window. `dayWeekBodyHeight` is in the dep array so this re-fires once
    // the ResizeObserver populates the height and HOUR_HEIGHT upgrades.
    // #3534: derive from DAY_GRID_VISIBLE_HOURS (the shared grid bounds the views
    // render with) so this can't drift from END_HOUR again — a hardcoded 22 here
    // divided by the rendered 24 and overshot the 8 AM scroll target.
    const hourPx = computeHourHeight(isMobile, isExpandedView, dayWeekBodyHeight, DAY_GRID_VISIBLE_HOURS);
    const target = 8 * hourPx;

    // Poll until scrollable (content rendered)
    let done = false;
    const tryScroll = () => {
      if (el.scrollHeight > el.clientHeight) {
        el.scrollTop = target;
        done = el.scrollTop > 0;
      }
    };
    const poll = setInterval(() => { tryScroll(); if (done) clearInterval(poll); }, 50);
    const giveUp = setTimeout(() => clearInterval(poll), 3000);
    return () => { clearInterval(poll); clearTimeout(giveUp); };
  }, [timeFilter, calendarDate, isMobile, isExpandedView, dayWeekBodyHeight]);

  // Simulated overscroll for day/week — same impulse-and-spring bounce as
  // before, but the spring is now driven by Framer Motion's `animate()` on a
  // MotionValue instead of a hand-rolled requestAnimationFrame decay loop.
  // The native rubber-band is disabled (overscroll-behavior:none).
  const bounceY = useMotionValue(0);

  // Apply the MotionValue to the grid's CONTENT (the gutter + day columns, i.e.
  // the scroll container's direct children) rather than the scroll container
  // itself — so the content bounces but the scrollbar stays put. Reads
  // timeGridRef.current live so it always targets the current grid node.
  useEffect(() => {
    const unsub = bounceY.on('change', (v) => {
      const el = timeGridRef.current;
      if (!el) return;
      const t = v ? `translateY(${v}px)` : '';
      for (let i = 0; i < el.children.length; i++) {
        (el.children[i] as HTMLElement).style.transform = t;
      }
    });
    return unsub;
  }, [bounceY]);

  // Wheel-edge bounce. Attaches via a poll-until-mounted loop (NOT just on first
  // run) so it binds even when the grid mounts after this effect runs — which is
  // exactly the cold-load / hard-refresh case where the bounce used to be dead.
  useEffect(() => {
    if (!(timeFilter === 'today' || timeFilter === 'week')) return;

    const MAX = 36; // px — peak overscroll give
    const MIN = 18; // px — minimum visible bounce
    const DAMP = 0.34; // wheel delta → impulse size
    // Smooth spring back, no overshoot. Lower stiffness = slightly slower return.
    const SPRING = { type: 'spring' as const, stiffness: 140, damping: 26, restDelta: 0.4 };

    let el: HTMLDivElement | null = null;
    let armed = true; // one bounce per edge contact; re-armed when off the edge
    let controls: { stop: () => void } | null = null;

    const onWheel = (e: WheelEvent) => {
      if (!el) return;
      // Not yet a real scroller (cold mount before layout settles): do nothing.
      if (el.scrollHeight <= el.clientHeight + 1) return;
      const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight - 1;
      const atTop = el.scrollTop <= 0;
      const pushingDown = e.deltaY > 0 && atBottom;
      const pushingUp = e.deltaY < 0 && atTop;
      if (!pushingDown && !pushingUp) {
        armed = true;
        return;
      }
      e.preventDefault();
      if (!armed) return;
      armed = false;
      const dir = pushingDown ? -1 : 1;
      const impulse = dir * Math.max(MIN, Math.min(MAX, Math.abs(e.deltaY) * DAMP));
      controls?.stop();
      bounceY.set(impulse);
      controls = animate(bounceY, 0, SPRING);
    };

    // Poll until the grid element exists, then attach once.
    let poll: ReturnType<typeof setInterval> | null = null;
    const attach = () => {
      const node = timeGridRef.current;
      if (!node || el) return;
      el = node;
      el.addEventListener('wheel', onWheel, { passive: false });
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
    };
    attach();
    if (!el) poll = setInterval(attach, 50);

    return () => {
      if (poll) clearInterval(poll);
      if (el) el.removeEventListener('wheel', onWheel);
      controls?.stop();
      bounceY.set(0);
      const node = timeGridRef.current;
      if (node) {
        for (let i = 0; i < node.children.length; i++) {
          (node.children[i] as HTMLElement).style.transform = '';
        }
      }
    };
  }, [timeFilter, calendarDate, isMobile, isExpandedView, bounceY]);

  // Persist the selected view. localStorage is the SOURCE OF TRUTH for surviving
  // the remount that happens when you navigate away and back (EventListModule is
  // keyed by module.id) — a synchronous write with no async round-trip to race
  // against. We ALSO best-effort-write it to the conversation module config so
  // the preference follows the user across devices; that write is fire-and-forget
  // and can't affect the local view (localStorage already settled it), which is
  // what made the old config-only approach flaky.
  const timeFilterDidMountRef = useRef(false);
  useEffect(() => {
    try {
      window.localStorage.setItem(timeFilterStorageKey, timeFilter);
    } catch { /* ignore storage access errors */ }
    // Skip the initial mount — only sync to the server on an actual change.
    if (!timeFilterDidMountRef.current) {
      timeFilterDidMountRef.current = true;
      return;
    }
    if (!conversationId) return;
    void updateConversationModules(conversationId, (current) =>
      current.map((m) =>
        m.id === moduleId ? { ...m, config: { ...((m as any).config || {}), timeFilter } } : m,
      ),
    ).catch(() => { /* non-fatal: localStorage is authoritative for the local view */ });
    // conversationId/moduleId are read inside the effect; include them so a
    // change can't write the preference into a stale conversation's config.
  }, [timeFilter, timeFilterStorageKey, conversationId, moduleId]);

  return {
    dayWeekBodyHeight,
    monthCellWidth,
    isExpandedView,
    calendarDate,
    setCalendarDate,
    timeFilter,
    setTimeFilter,
    expandedDate,
    setExpandedDate,
    timeGridRef,
    dayWeekContainerRef,
    monthGridRef,
    calendarContainerRef,
    swipeRef,
    moduleRootRef,
  };
}
